import { Link, useRouterState } from "@tanstack/react-router";
import {
  CalendarSearch,
  LayoutDashboard,
  type LucideIcon,
  LogOut,
  Menu,
  NotebookPen,
  UsersRound,
  Settings,
  Shield,
  User,
} from "lucide-react";
import type { ReactNode } from "react";

// Relative: vite.config.ts aliases only a fixed list of top-level folders and
// `features` is not one (see the same note in RequireAuth).
import { useSession, type Session } from "../features/auth/session";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "../utils/cn";
import { BrandMark } from "./BrandMark";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

/**
 * The application shell from docs/design-guidelines.md: a 240px sidebar rail
 * with the logo, icon nav and the user block at the bottom, beside a sticky
 * glass header and a gradient content column capped at 1400px.
 *
 * The rail is what makes this an application rather than a page, and it is the
 * FIRST thing the guidelines' layout section specifies. Below the desktop
 * breakpoint it collapses into a sheet, because a 240px rail on a phone leaves
 * nothing for the content the rail exists to navigate.
 *
 * To adopt: replace NAV with your real destinations and BrandMark with the
 * product's logo. `identity` arrives from RequireAuth, which is what puts a
 * user in the block at the bottom.
 *
 * THE RAIL. It is Brand Blue darkened in both themes, and it is built from the FILL
 * tokens (hb-dusk-deep to hb-blue-fill), which do not change with the theme. It was
 * derived from --hb-blue before, which .dark lightens so it can serve as text: the
 * rail then came out at about 51% lightness and its white/75 labels fell to 3.3:1.
 * On the fills the lowest label contrast is 5.3:1, in either theme.
 *
 * LAYERING. The shell is page furniture and sits BELOW anything portalled to
 * the body: rail 40, header 30, against 50 for dialogs, sheets, selects and
 * tooltips. The header briefly held 100, which put it over every one of them —
 * the attendee profile sheet opened with its avatar and its close button behind
 * the top bar, and the sheet's dimming overlay stopped at the bar's edge. A
 * number above 50 here is a bug in something else.
 */

const RAIL_CLASS =
  "border-r border-white/10 bg-[linear-gradient(180deg,hsl(var(--hb-dusk-deep))_0%,hsl(var(--hb-blue-fill))_100%)] text-white shadow-xl";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Absent means everyone. Both gated pages refuse the user themselves, so
   *  this only decides whether the rail advertises a refusal. */
  visibleTo?: (session: Session) => boolean;
}

// Only destinations that exist, and only the ones this user can use. A rail
// advertising a page that answers "Access denied" is worse than a short rail.
// No "/" entry: that route redirects here.
const NAV: NavItem[] = [
  { label: "My Events", to: "/dashboard", icon: LayoutDashboard },
  // Every published event, to everyone signed in (D12). No visibleTo: the
  // `list` rule on public_events asks for nothing beyond a signed-in caller,
  // because D1 makes the domain allowlist the gate at sign-in.
  { label: "All Events", to: "/events", icon: CalendarSearch },
  // Private notes about other attendees, readable after the event ends.
  { label: "My notes", to: "/notes", icon: NotebookPen },
  // People met by QR at events (TNG-93), across events.
  { label: "Connections", to: "/connections", icon: UsersRound },
  { label: "Profile", to: "/profile", icon: User },
  {
    label: "Administration",
    to: "/admin",
    icon: Settings,
    // Event admins are not platform admins — the common case for this page is
    // someone who runs one event and holds no platform role at all.
    visibleTo: (s) =>
      s.administersAnyEvent ||
      s.platformRole === "admin" ||
      s.platformRole === "super_admin",
  },
  {
    label: "Governance",
    to: "/governance",
    icon: Shield,
    visibleTo: (s) => s.platformRole === "super_admin",
  },
];

export function AppShell({
  identity,
  onSignOut,
  title,
  actions,
  children,
}: {
  identity?: { email: string | null } | null;
  /** Provided by the auth gate. Absent means no sign-out is rendered — the
   *  shell knows nothing about Firebase, so it stays renderable (and testable)
   *  without one. */
  onSignOut?: () => void;
  /** Page title, in the sticky header. Falls back to the product name. */
  title?: string;
  /** Page-level action, right-aligned in the header (guidelines). */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gradient-page">
      <aside
        className={cn(
          RAIL_CLASS,
          "fixed inset-y-0 left-0 z-40 hidden w-60 flex-col lg:flex",
        )}
        data-testid="app-rail"
      >
        <RailContent identity={identity} onSignOut={onSignOut} />
      </aside>

      <div className="lg:pl-60">
        <header
          className="glass-header sticky top-0 z-30 px-5 py-3 sm:py-4 md:px-10 md:py-5"
          data-app-header
        >
          {/* One row on every width: menu, title, page actions, inbox. Below
              `sm` a title, a status badge, a page action and the inbox do not
              fit on one line (a 390px phone left the title about seven
              characters), so there the page actions alone wrap onto a second
              row, under the title. The menu, the title and the inbox stay on
              the first row: the bell on a row of its own read as a bug and
              cost the phone header about 45px. */}
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
            data-testid="app-header-row"
          >
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Open navigation"
                  data-testid="open-navigation"
                  className="h-11 w-11 shrink-0 lg:hidden"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className={cn(RAIL_CLASS, "w-60 p-0")}>
                <RailContent identity={identity} onSignOut={onSignOut} />
              </SheetContent>
            </Sheet>

            <h1
              className="font-display min-w-0 flex-1 truncate text-xl font-semibold text-foreground md:text-2xl"
              title={title ?? "Holtzbrinck Connect"}
            >
              {title ?? "Holtzbrinck Connect"}
            </h1>
            {/* Structural, not an `actions` slot: the inbox belongs to every
                authed page, and it renders nothing without a session.

                Before the actions in the DOM, so that on a phone, where the
                actions wrap to a second row, Tab goes menu, bell, actions in
                reading order. From `sm` up `order-last` draws it after the
                actions, keeping the bell at the far right; there Tab reaches
                the bell one stop before the actions beside it. */}
            <div className="flex shrink-0 items-center empty:hidden sm:order-last">
              {/* 44px on a phone, 40px from `sm` up like the bell beside it, so the
                  header keeps its one row. */}
              <ThemeToggle className="sm:size-10" />
              <NotificationBell />
            </div>
            {/* Below `sm` a full row of its own, indented to line up with the
                title (the 44px menu button plus the 12px gap).

                Hidden while it holds nothing visible, because an empty item
                still opens a second flex line and its row gap. `:empty` alone
                misses the event page, whose actions are a wrapper div that can
                end up holding only a closed dialog, which renders nothing. The
                selector counts an element with children as visible, so two
                kinds of actions would be hidden wrongly: bare text with no
                element around it, and a lone void element such as an <img> or
                an <input>. Wrap either in a span. A browser without `:has()`
                drops the rule and shows the empty slot, which costs a 4px row
                gap on a phone and nothing else. */}
            <div
              className="flex min-w-0 items-center gap-2 max-sm:basis-full max-sm:pl-14 sm:ml-auto sm:shrink-0 [&:not(:has(:not(:empty)))]:hidden"
              data-testid="app-header-actions"
            >
              {actions}
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1400px] px-5 pb-20 pt-7 md:px-8 md:pt-12">
          {children}
        </main>
      </div>
    </div>
  );
}

function RailContent({
  identity,
  onSignOut,
}: {
  identity?: { email: string | null } | null;
  onSignOut?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const session = useSession();
  // No session means no provider: the sign-in screen and component tests. A
  // shell with nobody signed in advertises nothing role-gated.
  const items = NAV.filter(
    (item) => !item.visibleTo || (session !== null && item.visibleTo(session)),
  );

  return (
    <>
      <div className="flex items-center gap-3 px-5 py-5">
        <BrandMark className="rounded-sm bg-white p-1" />
        <div className="min-w-0">
          <p
            className="font-display truncate text-sm font-semibold text-white"
            data-testid="logo-text"
          >
            Holtzbrinck Connect
          </p>
        </div>
      </div>

      <nav className="flex flex-1 flex-col gap-1 px-3">
        {items.map((item) => {
          // Prefix match, so an event detail page still highlights the section
          // it belongs to.
          const active = pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? "page" : undefined}
              className={cn(
                // The active item carries a light-blue bar on its left edge as well as
                // a lighter fill, so it does not rely on colour alone. The bar is a
                // brand constant, not a token that flips with the theme: it sits on
                // the rail, which is dark in both.
                "flex items-center gap-3 rounded-md border-l-[3px] py-2 pl-2.5 pr-3 text-sm font-medium transition-colors max-lg:min-h-11",
                active
                  ? "border-hb-light-blue bg-white/15 text-white shadow-card"
                  : "border-transparent text-white/75 hover:bg-white/10 hover:text-white",
              )}
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {identity && (
        <div className="border-t border-white/15 px-3 py-4">
          <div className="flex items-center gap-3 px-2">
            <Avatar email={identity.email} />
            <p className="min-w-0 flex-1 truncate text-xs text-white/70">
              {identity.email}
            </p>
          </div>
          {/* Sign-out sits with the identity it ends: "who am I acting as"
              and "stop being them" are the same question. */}
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              data-testid="sign-out"
              className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm max-lg:min-h-11 font-medium text-white/75 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-[18px] w-[18px]" />
              Sign out
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** Small uppercase section label — level 2 of the guidelines' hierarchy. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {children}
    </h2>
  );
}

function Avatar({ email }: { email: string | null }) {
  const initials = (email ?? "?").slice(0, 2).toUpperCase();
  return (
    // The rail is dark everywhere now, so the old navy chip disappeared into
    // it. White/15 is the same treatment the active nav item gets.
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-xs font-bold text-white"
    >
      {initials}
    </span>
  );
}
